// Angular
import { Component, OnInit, Inject, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
// RxJS
import { Observable, of, Subscription } from 'rxjs';
// Lodash
import { each, find, some, orderBy } from 'lodash-es';
// NGRX
import { Update } from '@ngrx/entity';
import { Store, select } from '@ngrx/store';
// State
import { AppState } from '../../../../../core/reducers';
// Services and Models
import {
  currentUserRoleIds,
  RoleTemplate,
  Permission,
  selectRoleTemplateById,
  RoleTemplateOnServerCreated,
  RoleTemplateOnServerUpdated,
  selectRoleTemplateError,
  selectAllNonSuperAdminPermissions,
  currentUserPermissions,
  currentUser,
  RoleTemplatesPageRequested,
  selectRoleTemplateLatestSuccessfullAction,
  RoleTemplateActionTypes,
} from '../../../../../core/auth';
import { skip } from 'rxjs/operators';
import { CopyRoleTemplatePermissionDialogComponent } from '../copy-role-template-permission-dialog/copy-role-template-permission-dialog.component';
import { CopyRolePermissionDialogComponent } from '../../roles/copy-role-permission-dialog/copy-role-permission-dialog.component';
import { TwixorService } from '../../../../../core/services/twixor.service';

class FieldErrorStateMatcher implements ErrorStateMatcher {
  constructor(private isInvalidFn: (control: FormControl | null) => boolean) {}

  isErrorState(_control: FormControl | null): boolean {
    return this.isInvalidFn(_control);
  }
}

@Component({
  selector: 'kt-role-template-edit-dialog',
  templateUrl: './role-template-edit.dialog.component.html',
  styleUrls: ['./role-template-edit.dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class RoleTemplateEditDialogComponent implements OnInit, OnDestroy {
  // Public properties
  roleTemplate: RoleTemplate;
  roleTemplate$: Observable<RoleTemplate>;
  hasFormErrors = false;
  hasSubmissionErrors = false;
  submissionErrorObj: any;
  viewLoading = false;
  loadingAfterSubmit = false;
  isSuperAdmin: boolean;
  allPermissions$: Observable<Permission[]>;
  roleTemplatePermissions: Permission[] = [];
  roleTemplateTitle;
  // Private properties
  private subscriptions: Subscription[] = [];
  showCopyPermissions = true;
  showCopyRoleTemplatePermissions = true;
  departments = [];
  currentUser;

  /**
   * Component constructor
   *
   * @param dialogRef: MatDialogRef<RoleTemplateEditDialogComponent>
   * @param data: any
   * @param store: Store<AppState>
   * @param cdr: ChangeDetectorRef
   */
  constructor(
    public dialogRef: MatDialogRef<RoleTemplateEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private store: Store<AppState>,
    private dialog: MatDialog,
    private twixorService: TwixorService,
  ) {}

  /**
   * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
   */

  /**
   * On init
   */
  ngOnInit() {
    this.subscriptions.push(
      this.store.pipe(select(currentUser)).subscribe((res) => (this.currentUser = res)),
    );
    if (this?.currentUser?.userType !== 'Super Admin')
      this.twixorService.getDepartments().subscribe((res) => {
        this.departments = [{ id: '', name: '' }, ...res];
      });
    this.subscriptions.push(
      this.store
        .pipe(select(currentUserRoleIds))
        .subscribe((res) => (this.isSuperAdmin = res && res.includes(1))),
    );
    this.roleTemplateTitle = this.data.roleTemplateTitle;
    if (this.data.roleTemplateId) {
      this.roleTemplate$ = this.store.pipe(
        select(selectRoleTemplateById(this.data.roleTemplateId)),
      );
      this.showCopyPermissions = false;
      this.showCopyRoleTemplatePermissions = false;
    } else {
      const newRoleTemplate = new RoleTemplate();
      newRoleTemplate.clear();
      this.roleTemplate$ = of(newRoleTemplate);
    }

    this.roleTemplate$.subscribe((res) => {
      if (!res) {
        return;
      }
      this.roleTemplate = new RoleTemplate();
      this.roleTemplate.id = res.id;
      this.roleTemplate.title = res.title;
      this.roleTemplate.permissions = res.permissions;

      if (this.isSuperAdmin) {
        this.allPermissions$ = this.store.pipe(select(selectAllNonSuperAdminPermissions));
      } else {
        this.allPermissions$ = this.store.pipe(select(currentUserPermissions));
      }
      this.loadPermissions();
    });
    this.subscriptions.push(
      this.store.pipe(select(selectRoleTemplateError), skip(1)).subscribe((error) => {
        if (error) {
          if (error.error && error.error.code) {
            this.submissionErrorObj = error.error.errors;
          } else {
            this.submissionErrorObj = [{ message: 'GLOBAL.UNSPECIFIED_ERROR' }];
          }
          this.hasSubmissionErrors = true;
        }
      }),
    );
    this.subscriptions.push(
      this.store
        .pipe(select(selectRoleTemplateLatestSuccessfullAction), skip(1))
        .subscribe((res) => {
          if (res && res.action_type) {
            if (
              res.action_type === RoleTemplateActionTypes.RoleTemplateCreated ||
              res.action_type === RoleTemplateActionTypes.RoleTemplateUpdated
            ) {
              this.submissionErrorObj = null;
              this.hasSubmissionErrors = false;
              this.store.dispatch(new RoleTemplatesPageRequested({ page: this.data.queryParams }));
              this.dialogRef.close();
            }

            this.viewLoading = false;
          }
        }),
    );

    this.subscriptions.push(
      this.store
        .pipe(select(selectRoleTemplateError), skip(1))
        .subscribe((_) => (this.viewLoading = false)),
    );
  }

  /**
   * On destroy
   */
  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Load permissions
   */
  loadPermissions() {
    this.subscriptions.push(
      this.allPermissions$.subscribe((_allPermissions) => {
        if (!_allPermissions) {
          return;
        }

        const mainPermissions = _allPermissions.filter((el) => !el.parentId);
        mainPermissions.forEach((element: Permission) => {
          const hasUserPermission = this.roleTemplate.permissions.some((t) => t === element.id);
          const rootPermission = new Permission();
          rootPermission.clear();
          rootPermission.isSelected = hasUserPermission;
          rootPermission._children = [];
          rootPermission.id = element.id;
          rootPermission.level = element.level;
          rootPermission.parentId = element.parentId;
          rootPermission.title = element.title;
          const children = _allPermissions.filter(
            (el) => el.parentId && el.parentId === element.id,
          );
          children.forEach((child) => {
            const hasUserChildPermission = this.roleTemplate.permissions.some(
              (t) => t === child.id,
            );
            const childPermission = new Permission();
            childPermission.clear();
            childPermission.isSelected = hasUserChildPermission;
            childPermission._children = [];
            childPermission.id = child.id;
            childPermission.level = child.level;
            childPermission.parentId = child.parentId;
            childPermission.title = child.title;
            rootPermission._children.push(childPermission);
          });
          this.roleTemplatePermissions.push(rootPermission);
          this.roleTemplatePermissions = orderBy(this.roleTemplatePermissions, ['id']).reverse();
        });
      }),
    );
  }

  /** ACTIONS */
  /**
   * Returns permissions ids
   */
  preparePermissionIds(): number[] {
    const result = [];
    each(this.roleTemplatePermissions, (_root: Permission) => {
      if (_root.isSelected) {
        result.push(_root.id);
        each(_root._children, (_child: Permission) => {
          if (_child.isSelected) {
            result.push(_child.id);
          }
        });
      }
    });
    return result;
  }

  /**
   * Returns role template for save
   */
  prepareRole(): RoleTemplate {
    const _role = new RoleTemplate();
    _role.id = this.roleTemplate.id;
    _role.permissions = this.preparePermissionIds();
    _role.title = this.roleTemplate.title;

    for (const [key, value] of Object.entries(_role)) {
      if (typeof value === 'string') {
        _role[key] = value.trim();
      }
    }
    return _role;
  }

  /**
   * Save data
   */
  onSubmit() {
    this.hasFormErrors = false;
    this.loadingAfterSubmit = false;
    /** check form */
    if (this.isInvalid()) {
      this.hasFormErrors = true;
      return;
    }

    const editedRole = this.prepareRole();
    if (editedRole.id > 0) {
      this.updateRoleTemplate(editedRole);
    } else {
      this.createRoleTemplate(editedRole);
    }
  }

  /**
   * Update role template
   *
   * @param _role: RoleTemplate
   */
  updateRoleTemplate(_role: RoleTemplate) {
    this.loadingAfterSubmit = true;
    this.viewLoading = true;
    /* Server loading imitation. Remove this on real code */
    const updateRoleTemplate: Update<RoleTemplate> = {
      id: this.roleTemplate.id,
      changes: _role,
    };
    this.store.dispatch(
      new RoleTemplateOnServerUpdated({
        partialRoleTemplate: updateRoleTemplate,
        roleTemplate: _role,
      }),
    );
  }

  /**
   * Create role template
   *
   * @param _role: RoleTemplate
   */
  createRoleTemplate(_role: RoleTemplate) {
    this.loadingAfterSubmit = true;
    this.viewLoading = true;
    this.store.dispatch(new RoleTemplateOnServerCreated({ roleTemplate: _role }));
  }

  /**
   * Close alert
   *
   */
  onAlertClose() {
    this.hasFormErrors = false;
  }

  /**
   * Check is selected changes
   *
   * @param permission
   */
  isSelectedChanged(permission: Permission) {
    if (permission._children.length === 0 && permission.isSelected) {
      const _root = find(
        this.roleTemplatePermissions,
        (item: Permission) => item.id === permission.parentId,
      );
      if (_root && !_root.isSelected) {
        _root.isSelected = true;
      }
      return;
    }

    if (permission._children.length === 0 && !permission.isSelected) {
      const _root = find(
        this.roleTemplatePermissions,
        (item: Permission) => item.id === permission.parentId,
      );
      if (_root && _root.isSelected) {
        if (!some(_root._children, (item: Permission) => item.isSelected === true)) {
          _root.isSelected = false;
        }
      }
      return;
    }

    if (permission._children.length > 0 && permission.isSelected) {
      each(permission._children, (item: Permission) => (item.isSelected = true));
      return;
    }

    if (permission._children.length > 0 && !permission.isSelected) {
      each(permission._children, (item: Permission) => {
        item.isSelected = false;
      });
      return;
    }
  }

  /** UI */
  /**
   * Returns component title
   */
  getTitle(): string {
    if (this.roleTemplate && this.roleTemplate.id) {
      // tslint:disable-next-line:no-string-throw
      return 'ROLE_TEMPLATES.EDIT_ROLE_TEMPLATE';
    }

    // tslint:disable-next-line:no-string-throw
    return 'ROLE_TEMPLATES.NEW_ROLE_TEMPLATE';
  }

  /**
   * Returns is title valid
   */
  isInvalid(): boolean {
    return !this.roleTemplate.title?.trim();
  }

  titleMatcher = new FieldErrorStateMatcher((control) => {
    return (control.touched || this.hasFormErrors) && !this.roleTemplate.title?.trim();
  });

  getChildrenSorted(permissions) {
    return orderBy(permissions, ['title']).reverse();
  }

  onCopyPermissionClicked() {
    const dialogRef = this.dialog.open(CopyRolePermissionDialogComponent, {
      data: {},
      width: '450px',
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe((role) => {
      if (role) {
        this.roleTemplatePermissions.map((e) => {
          e.isSelected = role.indexOf(e.id) > -1;
          e._children.map((e1) => {
            e1.isSelected = role.indexOf(e1.id) > -1;
            return e1;
          });
          return e;
        });
      }
    });
  }

  onCopyRoleTemplatePermissionClicked() {
    const dialogRef = this.dialog.open(CopyRoleTemplatePermissionDialogComponent, {
      data: {},
      width: '450px',
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe((role) => {
      if (role) {
        this.roleTemplatePermissions.map((e) => {
          e.isSelected = role.indexOf(e.id) > -1;
          e._children.map((e1) => {
            e1.isSelected = role.indexOf(e1.id) > -1;
            return e1;
          });
          return e;
        });
      }
    });
  }

  normalizeTitle() {
    if (this.roleTemplate.title === '') return;

    let titleParts = this.roleTemplate.title.replace(/\s+/g, ' ').trim().split(' ');

    // Remove the word template if it exists at the end.
    if (titleParts[titleParts.length - 1].toLowerCase() === 'template') {
      titleParts = titleParts.slice(0, titleParts.length - 1);
    }

    // Add it at the end of the title.
    titleParts = [...titleParts, 'Template'];

    this.roleTemplate.title = titleParts.join(' ');
  }
}
